<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <LoaderApi :apiUrl="`portal/service-desk/well/${$route.params.id}`">
          <template slot-scope="wellData">
            <div v-if="wellData && wellData.data">
              <!-- Header do Poço -->
              <v-card class="well-header-card mb-6" elevation="3" :style="{ borderLeftColor: softenColor(wellData.data.category.color) }">
                <v-card-text class="pa-6">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-avatar 
                        :color="softenColor(wellData.data.category.color)" 
                        size="64"
                        class="mr-4 elevation-4"
                      >
                        <v-icon color="white" size="32">water_drop</v-icon>
                      </v-avatar>
                      <div>
                        <h1 class="text-h5 font-weight-bold mb-2" :style="{ color: softenColor(wellData.data.category.color) }">
                          {{ wellData.data.category.name }}
                        </h1>
                        <div class="d-flex align-center">
                          <div class="mr-3 id-container">
                            <span class="id-text">#{{ wellData.data.id }}</span>
                          </div>
                          <v-chip 
                            large
                            class="status-chip"
                            :style="{ 
                              background: `linear-gradient(135deg, ${wellData.data.status.color}, ${adjustColorBrightness(wellData.data.status.color, -20)})`,
                              color: 'white',
                              border: `2px solid ${adjustColorBrightness(wellData.data.status.color, -30)}`
                            }"
                          >
                            <v-icon left>{{ wellData.data.status.icon }}</v-icon>
                            {{ wellData.data.status.name }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Botão de Acessar Relatório -->
                    <div class="text-center">
                      <v-btn
                        x-large
                        class="report-btn elevation-4"
                        :style="{ background: `linear-gradient(135deg, ${softenColor(wellData.data.category.color)}, ${adjustColorBrightness(softenColor(wellData.data.category.color), -15)})` }"
                        @click="accessReport"
                      >
                        <v-icon left large>assessment</v-icon>
                        Acessar Relatório do Poço
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Informações Principais -->
              <v-row class="equal-height-cards">
                <!-- Coluna Esquerda - Informações Básicas -->
                <v-col cols="12" md="6" class="d-flex">
                  <v-card class="info-card mb-4" elevation="2" :style="{ borderLeft: `4px solid ${softenColor(wellData.data.category.color)}` }">
                    <v-card-title class="info-card-title" :style="{ background: `linear-gradient(135deg, ${softenColor(wellData.data.category.color)}, ${adjustColorBrightness(softenColor(wellData.data.category.color), -20)})` }">
                      <v-icon color="white" class="mr-2">info</v-icon>
                      <span class="text-subtitle-1">Informações Básicas</span>
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <div class="info-grid">
                        <div class="info-item id-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -10)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -10)" size="18" class="mr-2">tag</v-icon>
                            <span class="info-label">ID do Poço:</span>
                          </div>
                          <div class="id-background">
                            <span class="info-value">#{{ wellData.data.id }}</span>
                          </div>
                        </div>
                        <div class="info-item code-item" v-if="wellData.data.code" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -15)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -15)" size="18" class="mr-2">code</v-icon>
                            <span class="info-label">Código:</span>
                          </div>
                          <div class="code-background">
                            <span class="info-value">{{ wellData.data.code }}</span>
                          </div>
                        </div>
                        <div class="info-item date-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -20)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -20)" size="18" class="mr-2">play_circle_filled</v-icon>
                            <span class="info-label">Data de Início:</span>
                          </div>
                          <div class="date-container">
                            <span class="info-value">{{ wellData.data.date_start }}</span>
                          </div>
                        </div>
                        <div class="info-item date-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -25)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -25)" size="18" class="mr-2">check_circle</v-icon>
                            <span class="info-label">Data de Término:</span>
                          </div>
                          <div class="date-container">
                            <span class="info-value">{{ wellData.data.date_end }}</span>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Coluna Direita - Endereço e Observações -->
                <v-col cols="12" md="6" class="d-flex">
                  <!-- Endereço -->
                  <v-card class="info-card mb-4" elevation="2" v-if="wellData.data.address" :style="{ borderLeft: `4px solid ${softenColor(wellData.data.category.color)}` }">
                    <v-card-title class="info-card-title" :style="{ background: `linear-gradient(135deg, ${softenColor(wellData.data.category.color)}, ${adjustColorBrightness(softenColor(wellData.data.category.color), -20)})` }">
                      <v-icon color="white" class="mr-2">location_on</v-icon>
                      <span class="text-subtitle-1">Localização</span>
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <div class="address-details">
                        <div class="address-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -5)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -5)" size="18" class="mr-2">location_on</v-icon>
                            <span class="address-label">Endereço:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.address }}</span>
                        </div>
                        <div class="address-item" v-if="wellData.data.address.number" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -8)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -8)" size="18" class="mr-2">home</v-icon>
                            <span class="address-label">Número:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.number }}</span>
                        </div>
                        <div class="address-item" v-if="wellData.data.address.complement" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -12)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -12)" size="18" class="mr-2">add_location</v-icon>
                            <span class="address-label">Complemento:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.complement }}</span>
                        </div>
                        <div class="address-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -18)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -18)" size="18" class="mr-2">place</v-icon>
                            <span class="address-label">Bairro:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.district }}</span>
                        </div>
                        <div class="address-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -22)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -22)" size="18" class="mr-2">location_city</v-icon>
                            <span class="address-label">Cidade:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.city.name }}</span>
                        </div>
                        <div class="address-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -28)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -28)" size="18" class="mr-2">flag</v-icon>
                            <span class="address-label">Estado:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.city.state.name }} ({{ wellData.data.address.city.state.code }})</span>
                        </div>
                        <div class="address-item" :style="{ borderLeft: `3px solid ${adjustColorBrightness(softenColor(wellData.data.category.color), -35)}` }">
                          <div class="label-with-icon">
                            <v-icon :color="adjustColorBrightness(softenColor(wellData.data.category.color), -35)" size="18" class="mr-2">markunread_mailbox</v-icon>
                            <span class="address-label">CEP:</span>
                          </div>
                          <span class="address-value">{{ wellData.data.address.zip_code }}</span>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- Observações -->
                  <v-card class="info-card mb-4" elevation="2" v-if="wellData.data.observation" :style="{ borderLeft: `4px solid ${softenColor(wellData.data.category.color)}` }">
                    <v-card-title class="info-card-title" :style="{ background: `linear-gradient(135deg, ${softenColor(wellData.data.category.color)}, ${adjustColorBrightness(softenColor(wellData.data.category.color), -20)})` }">
                      <v-icon color="white" class="mr-2">note</v-icon>
                      <span class="text-subtitle-1">Observações</span>
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <p class="observation-text">{{ wellData.data.observation }}</p>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Histórico de Ordens de Serviço -->
              <v-card class="timeline-card mb-4" elevation="2" :style="{ borderLeft: `4px solid ${softenColor(wellData.data.category.color)}` }">
                <v-card-title class="timeline-card-title" :style="{ background: `linear-gradient(135deg, ${softenColor(wellData.data.category.color)}, ${adjustColorBrightness(softenColor(wellData.data.category.color), -20)})` }">
                  <v-icon color="white" class="mr-2">history</v-icon>
                  <span class="text-subtitle-1">Histórico de Ordens de Serviço</span>
                  <v-spacer></v-spacer>
                  <v-chip 
                    color="white" 
                    small 
                    class="timeline-count-chip"
                    :style="{ background: `rgba(255, 255, 255, 0.2)`, color: 'white' }"
                  >
                    {{ wellData.data.orders && wellData.data.orders.orders ? wellData.data.orders.orders.length : 0 }} {{ wellData.data.orders && wellData.data.orders.orders && wellData.data.orders.orders.length === 1 ? 'ordem' : 'ordens' }}
                  </v-chip>
                </v-card-title>
                <v-card-text class="pa-4">
                  <div v-if="wellData.data.orders && wellData.data.orders.orders && wellData.data.orders.orders.length > 0" class="timeline-container">
                    <div 
                      v-for="(order, index) in wellData.data.orders.orders" 
                      :key="order.id"
                      class="timeline-item"
                      :class="{ 'timeline-item-last': index === wellData.data.orders.orders.length - 1 }"
                    >
                      <!-- Timeline Line -->
                      <div class="timeline-line" :style="{ background: softenColor(wellData.data.category.color) }"></div>
                      
                      <!-- Timeline Dot -->
                      <div class="timeline-dot" :style="{ 
                        background: `linear-gradient(135deg, ${order.status.color}, ${adjustColorBrightness(order.status.color, -20)})`,
                        border: `3px solid ${softenColor(wellData.data.category.color)}`
                      }">
                        <v-icon color="white" size="16">{{ order.status.icon }}</v-icon>
                      </div>
                      
                      <!-- Timeline Content -->
                      <div class="timeline-content" @click="goToOrderDetails(order.uuid)">
                        <v-card class="timeline-order-card" elevation="2" :style="{ borderLeft: `4px solid ${order.status.color}` }">
                          <v-card-text class="pa-4">
                            <div class="d-flex align-center justify-space-between">
                              <div class="d-flex align-center">
                                <v-avatar 
                                  :color="softenColor(order.category.color)" 
                                  size="40"
                                  class="mr-3"
                                >
                                  <v-icon color="white" size="20">{{ order.category.icon }}</v-icon>
                                </v-avatar>
                                <div>
                                  <h4 class="text-subtitle-1 font-weight-bold mb-1" :style="{ color: softenColor(order.category.color) }">
                                    {{ order.category.name }}
                                  </h4>
                                  <div class="d-flex align-center">
                                    <span class="order-id-text mr-2">#{{ order.id }}</span>
                                    <v-chip 
                                      small
                                      class="order-status-chip"
                                      :style="{ 
                                        background: `linear-gradient(135deg, ${order.status.color}, ${adjustColorBrightness(order.status.color, -20)})`,
                                        color: 'white'
                                      }"
                                    >
                                      <v-icon left size="14">{{ order.status.icon }}</v-icon>
                                      {{ order.status.name }}
                                    </v-chip>
                                  </div>
                                </div>
                              </div>
                              <div class="timeline-dates">
                                <div class="timeline-date-item">
                                  <v-icon size="14" class="mr-1" :color="adjustColorBrightness(softenColor(wellData.data.category.color), -20)">play_circle_filled</v-icon>
                                  <span class="timeline-date-text">{{ order.date_start }}</span>
                                </div>
                                <div class="timeline-date-item" v-if="order.date_end">
                                  <v-icon size="14" class="mr-1" :color="adjustColorBrightness(softenColor(wellData.data.category.color), -20)">check_circle</v-icon>
                                  <span class="timeline-date-text">{{ order.date_end }}</span>
                                </div>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Estado vazio quando não há ordens -->
                  <div v-else class="empty-timeline">
                    <v-container class="text-center py-8">
                      <v-icon size="60" color="grey lighten-2" class="mb-3">build</v-icon>
                      <h3 class="text-h6 grey--text mb-2">Nenhuma ordem de serviço</h3>
                      <p class="text-body-2 grey--text">
                        Este poço ainda não possui ordens de serviço cadastradas.
                      </p>
                    </v-container>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Ações -->
              <v-card class="actions-card" elevation="2">
                <v-card-text class="pa-4">
                  <div class="d-flex justify-center">
                    <v-btn
                      large
                      class="back-btn"
                      :style="{ 
                        background: `linear-gradient(135deg, ${softenColor(wellData.data.category.color)}, ${adjustColorBrightness(softenColor(wellData.data.category.color), -15)})`,
                        color: 'white'
                      }"
                      @click="goBack"
                    >
                      <v-icon left>arrow_back</v-icon>
                      Voltar
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Estado de carregamento -->
            <div v-else class="loading-state">
              <v-container class="text-center py-12">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                  class="mb-4"
                ></v-progress-circular>
                <h3 class="text-h6 grey--text">Carregando detalhes do poço...</h3>
              </v-container>
            </div>
          </template>
        </LoaderApi>
      </v-col>
    </v-row>

    <!-- Dialog para visualizar PDF -->
    <v-dialog
      v-model="showPdfDialog"
      max-width="95vw"
      max-height="95vh"
      persistent
      scrollable
    >
      <v-card class="pdf-dialog">
        <v-card-title class="pdf-dialog-header">
          <v-icon color="white" class="mr-2">description</v-icon>
          <span>Relatório do Poço</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="closePdfDialog"
            color="white"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pdf-dialog-content pa-0">
          <iframe
            v-if="pdfUrl"
            :src="pdfUrl"
            class="pdf-iframe"
            frameborder="0"
            allowfullscreen
          ></iframe>
        </v-card-text>
        
        <v-card-actions class="pdf-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="openPdfInNewTab"
            class="mr-2"
          >
            <v-icon left>open_in_new</v-icon>
            Abrir em Nova Aba
          </v-btn>
          <v-btn
            color="grey"
            @click="closePdfDialog"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import LoaderApi from "@/default/components/LoaderApi/LoaderApi";

export default {
  name: "Well",
  
  data() {
    return {
      showPdfDialog: false,
      pdfUrl: null
    };
  },
  
  components: {
    LoaderApi
  },
  
  methods: {
    adjustColorBrightness(color, percent) {
      // Remove o # se existir
      const hex = color.replace('#', '');
      
      // Converte para RGB
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Ajusta o brilho
      const newR = Math.max(0, Math.min(255, r + (r * percent / 100)));
      const newG = Math.max(0, Math.min(255, g + (g * percent / 100)));
      const newB = Math.max(0, Math.min(255, b + (b * percent / 100)));
      
      // Converte de volta para hex
      const toHex = (n) => {
        const hex = Math.round(n).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };
      
      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    },
    
    // Função para suavizar cores muito fortes mantendo boa legibilidade
    softenColor(color) {
      // Remove o # se existir
      const hex = color.replace('#', '');
      
      // Converte para RGB
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calcula o brilho da cor (luminância)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Se a cor for muito brilhante (luminância > 0.7), suaviza ela
      if (luminance > 0.7) {
        // Mistura com uma cor mais escura para reduzir o brilho
        const darkenFactor = 0.3; // 30% mais escuro
        const newR = Math.round(r * (1 - darkenFactor));
        const newG = Math.round(g * (1 - darkenFactor));
        const newB = Math.round(b * (1 - darkenFactor));
        
        // Converte de volta para hex
        const toHex = (n) => {
          const hex = Math.round(n).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        };
        
        return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
      }
      
      // Se a cor for muito escura (luminância < 0.3), clareia ela
      if (luminance < 0.3) {
        // Mistura com branco para clarear
        const lightenFactor = 0.4; // 40% mais claro
        const newR = Math.round(r + (255 - r) * lightenFactor);
        const newG = Math.round(g + (255 - g) * lightenFactor);
        const newB = Math.round(b + (255 - b) * lightenFactor);
        
        // Converte de volta para hex
        const toHex = (n) => {
          const hex = Math.round(n).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        };
        
        return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
      }
      
      // Se a cor estiver em um bom range, retorna a cor original
      return color;
    },
    
    goBack() {
      this.$router.go(-1);
    },
    
    goToOrderDetails(orderUuid) {
      // Navegar para a página de detalhes da ordem
      this.$router.push({ 
        name: 'order', 
        params: { id: orderUuid } 
      });
    },
    
    accessReport() {
      const wellId = this.$route.params.id;
      
      this.$Api.post({
        url: `portal/service-desk/well/${wellId}/document-pdf`,
        data: { well_id: wellId },
        success: (response) => {
          if (response?.data?.data?.file_url) {
            this.pdfUrl = response.data.data.file_url;
            this.showPdfDialog = true;
          }
        },
        error: (error) => {
          console.error('Erro ao gerar relatório:', error);
          this.errorMessage();
        },
        config: {
          message: {
            loading: {
              show: true,
              title: '📄 Gerando relatório do Poço...',
              text: 'Estamos preparando seu relatório em PDF. Isso pode levar alguns segundos.'
            },
            success: {
              show: true,
              title: 'Relatório gerado com sucesso!',
              text: 'Seu relatório foi criado com sucesso.'
            },
            error: {
              show: true,
              title: 'Erro ao gerar relatório',
              text: 'Não foi possível gerar o relatório no momento. Verifique sua conexão e tente novamente.'
            }
          }
        }
      });
    },
    
    closePdfDialog() {
      this.showPdfDialog = false;
      this.pdfUrl = null;
    },
    
    openPdfInNewTab() {
      if (this.pdfUrl) {
        window.open(this.pdfUrl, '_blank');
      }
    },
    
    errorMessage() {
      this.$swal({
        title: '❌ Ops! Algo deu errado',
        text: 'Não foi possível processar sua solicitação no momento. Por favor, tente novamente em alguns instantes.',
        icon: 'error',
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#f44336'
      });
    }
  }
};
</script>

<style scoped>
/* Header do Poço */
.well-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px !important;
  border-left: 6px solid #1976D2;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.3s ease;
}

.well-header-card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12) !important;
}

/* Status chip melhorado */
.status-chip {
  padding: 8px 20px !important;
  border-radius: 20px !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
}

.status-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

/* Container do ID no header */
.id-container {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05));
  border-radius: 12px;
  padding: 8px 16px;
  border: 1px solid rgba(25, 118, 210, 0.2);
  height: 40px;
  display: flex;
  align-items: center;
}

.id-text {
  color: #1976D2 !important;
  font-weight: 700 !important;
  font-size: 1.1rem !important;
}

/* Botão de relatório em destaque */
.report-btn {
  border-radius: 16px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border: none !important;
}

.report-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25) !important;
  filter: brightness(1.1);
}

/* Cards de informação com altura igual */
.equal-height-cards .v-col {
  display: flex;
  flex-direction: column;
}

.equal-height-cards .info-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.equal-height-cards .info-card .v-card-text {
  flex: 1;
}

/* Cards de informação */
.info-card {
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06) !important;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.12) !important;
}

.info-card-title {
  color: white !important;
  border-radius: 12px 12px 0 0 !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Grid de informações */
.info-grid {
  display: grid;
  gap: 16px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 8px;
  border-left: 3px solid #1976D2;
  min-height: 48px;
}

.label-with-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 140px;
  justify-content: flex-start;
}

.info-label {
  color: #666;
  font-weight: 500;
  font-size: 0.9rem;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
  text-align: right;
  flex: 1;
}

/* ID e Código com background */
.id-item .id-background {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05));
  border-radius: 8px;
  padding: 6px 12px;
  border: 1px solid rgba(25, 118, 210, 0.2);
  display: inline-block;
}

.id-item .info-value {
  color: #1976D2 !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
}

.code-item .code-background {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05));
  border-radius: 8px;
  padding: 6px 12px;
  border: 1px solid rgba(76, 175, 80, 0.2);
  display: inline-block;
}

.code-item .info-value {
  color: #4CAF50 !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
}

/* Datas destacadas */
.date-item {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.1)) !important;
  border-radius: 12px !important;
  border-left: 3px solid #4CAF50 !important;
  transition: all 0.3s ease;
}

.date-item:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.15)) !important;
  transform: translateY(-1px);
}

.date-container {
  display: flex;
  align-items: center;
  width: 100%;
}

/* Container de endereço */
.address-details {
  display: grid;
  gap: 12px;
}

.address-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 8px;
  border-left: 3px solid #1976D2;
  min-height: 48px;
}

.address-label {
  color: #666;
  font-weight: 500;
  font-size: 0.9rem;
}

.address-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
  text-align: right;
  flex: 1;
}

/* Observações */
.observation-text {
  color: #2c3e50;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  padding: 16px;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 8px;
  border-left: 4px solid #1976D2;
}

/* Timeline de Ordens de Serviço */
.timeline-card {
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06) !important;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
}

.timeline-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.12) !important;
}

.timeline-card-title {
  color: white !important;
  border-radius: 12px 12px 0 0 !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.timeline-count-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  transition: all 0.3s ease !important;
  font-size: 0.75rem !important;
}

/* Timeline Container */
.timeline-container {
  position: relative;
  padding-left: 0;
}

.timeline-item {
  position: relative;
  margin-bottom: 24px;
  padding-left: 0;
}

.timeline-item-last {
  margin-bottom: 0;
}

/* Timeline Line */
.timeline-line {
  position: absolute;
  left: 20px;
  top: 40px;
  width: 2px;
  height: calc(100% + 24px);
  background: #1976D2;
  border-radius: 1px;
  z-index: 1;
}

.timeline-item-last .timeline-line {
  display: none;
}

/* Timeline Dot */
.timeline-dot {
  position: absolute;
  left: 12px;
  top: 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Timeline Content */
.timeline-content {
  margin-left: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.timeline-content:hover {
  transform: translateX(4px);
}

.timeline-order-card {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
}

.timeline-order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.order-id-text {
  color: #1976D2 !important;
  font-weight: 700 !important;
  font-size: 0.9rem !important;
}

.order-status-chip {
  border-radius: 12px !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  height: 24px !important;
  display: flex !important;
  align-items: center !important;
}

.timeline-dates {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.timeline-date-item {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
}

.timeline-date-text {
  color: #666;
  font-weight: 500;
}

/* Estado vazio da timeline */
.empty-timeline {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px;
  margin: 20px 0;
}

/* Card de ações */
.actions-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px !important;
  border: 2px solid #dee2e6;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.actions-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

/* Botão de voltar */
.back-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease !important;
}

.back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
  filter: brightness(1.1);
}

/* Estado de carregamento */
.loading-state {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  margin: 20px 0;
}

/* Dialog do PDF */
.pdf-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.pdf-dialog-header {
  background: linear-gradient(135deg, #1976D2, #42A5F5);
  color: white !important;
  padding: 16px 24px !important;
  font-weight: 600 !important;
}

.pdf-dialog-content {
  height: 80vh;
  padding: 0 !important;
  background: #f5f5f5;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: white;
}

.pdf-dialog-actions {
  background: #f8f9fa;
  padding: 16px 24px !important;
  border-top: 1px solid #e0e0e0;
}

/* Responsividade */
@media (max-width: 768px) {
  .well-header-card .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .well-header-card .text-center {
    width: 100%;
    margin-top: 16px;
  }
  
  .report-btn {
    width: 100%;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .address-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .label-with-icon {
    min-width: auto;
    margin-bottom: 8px;
  }
  
  .info-value {
    text-align: left;
  }
  
  .address-value {
    text-align: left;
  }
  
  .date-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .equal-height-cards .v-col {
    margin-bottom: 16px;
  }
  
  .status-chip {
    padding: 6px 16px !important;
    font-size: 0.9rem !important;
  }
  
  .id-container {
    padding: 6px 12px;
    height: auto;
  }
  
  .timeline-content {
    margin-left: 40px;
  }
  
  .timeline-dot {
    left: 10px;
    width: 18px;
    height: 18px;
  }
  
  .timeline-line {
    left: 18px;
  }
  
  .timeline-dates {
    align-items: flex-start;
    margin-top: 8px;
  }
}

@media (max-width: 480px) {
  .well-header-card {
    padding: 16px !important;
  }
  
  .info-card-title {
    padding: 12px 16px !important;
  }
  
  .info-card .v-card-text {
    padding: 16px !important;
  }
  
  .actions-card .v-card-text {
    padding: 16px !important;
  }
  
  .back-btn {
    width: 100%;
  }
  
  .timeline-content {
    margin-left: 35px;
  }
  
  .timeline-dot {
    left: 8px;
    width: 16px;
    height: 16px;
  }
  
  .timeline-line {
    left: 15px;
  }
  
  .pdf-dialog-content {
    height: 70vh;
  }
  
  .pdf-dialog-header {
    padding: 12px 16px !important;
  }
  
  .pdf-dialog-actions {
    padding: 12px 16px !important;
    flex-direction: column;
    gap: 8px;
  }
  
  .pdf-dialog-actions .v-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .pdf-dialog-content {
    height: 60vh;
  }
}
</style>
